import React, { useState, useEffect, useRef } from "react";
import Loading from "../../../../Utils/Loading/Loading";
import TopHeader from "./TopHeader";
import useSize from "../../../../Utils/useSize/useSize";
import Backend from "../../../../Backend/Backend";
import moment from "moment";
import { toJpeg } from "html-to-image";
import toast from "react-hot-toast";

const fmt = require("indian-number-format");

const backend = await new Backend().init();

export default function SettlementScreen() {
  const windowSize = useSize();
  const elementRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [rent, setRent] = useState(0);
  const [profitloss, setProfitLoss] = useState(0);
  const [commission, setCommission] = useState(0);
  const [date_start, setDate_Start] = useState(moment().startOf("week"));
  const [date_end, setDate_End] = useState(moment().endOf("week"));

  const [isDollar, setIsDollar] = useState(0);

  useEffect(() => {
    loadSettlement(moment().startOf("week"), moment().endOf("week"));
  }, []);

  const loadSettlement = (a, b) => {
    setDate_Start(a);
    setDate_End(b);
    setLoading(true);

    const data = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
      start: a ? a : date_start,
      end: b ? b : date_end,
    };

    backend.settlement(data).then((r) => {
      setLoading(false);
      if (r.error === "False") {
        setProfitLoss(r.booked);
        setCommission(r.commission);
        setRent(r.rent || 0);
        setIsDollar(r.isDollar || 0);
      } else {
        if (r.logout === 1) {
          window.LogOut();
        } else {
          toast.error(r.message);
        }
      }
    });
  };

  const htmlToImageConvert = () => {
    if (!elementRef.current) return;
    toJpeg(elementRef.current, { cacheBust: false })
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.download = "Settlement.jpeg";
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => console.log(err));
  };

  const currency = isDollar === 1 ? "$" : "₹";

  return (
    <div
      style={{
        height: windowSize[1] - 100,
        overflowY: "auto",
      }}
      className="bg-[var(--background)]"
    >
      {/* 🔹 Header */}
      <TopHeader
        onDownload={htmlToImageConvert}
        loadSettlement={loadSettlement}
      />

      <div
        ref={elementRef}
        className="w-full bg-[var(--background)] max-w-2xl space-y-5 py-4 px-4 mx-auto"
      >
        {/* 📋 Summary Header */}
        <div className="bg-[var(--background)] border-2 border-[var(--hover)] text-[var(--text)] rounded-xl shadow-sm text-center py-3">
          <div className="font-semibold text-[15px]">
            USER: {localStorage.getItem("username")}
          </div>
          <div className="text-[12px] text-[var(--subtext)] mt-0.5">
            {moment(date_start).format("DD MMM YYYY")} →{" "}
            {moment(date_end).format("DD MMM YYYY")}
          </div>
        </div>

        {/* 📊 Main Data */}
        {loading ? (
          <div className="flex bg-[var(--background)] justify-center py-10">
            <Loading height={windowSize[1] - 370} />
          </div>
        ) : (
          <div className="space-y-5">
            {/* PROFIT SECTION */}
            <SectionCard title="PROFIT" color="green">
              <Table
                currency={currency}
                rent={rent}
                color="green"
                rows={[
                  {
                    user: "MASTER",
                    pnl: Math.abs(profitloss) + parseFloat(rent),
                    brk: commission,
                    rent: rent,
                    total: parseFloat(profitloss) + parseFloat(commission),
                  },
                  {
                    user: "TOTAL",
                    pnl: Math.abs(profitloss) + parseFloat(rent),
                    brk: commission,
                    rent: rent,
                    total: parseFloat(profitloss) + parseFloat(commission),
                  },
                ]}
              />
            </SectionCard>

            {/* LOSS SECTION */}
            <SectionCard title="LOSS" color="red">
              <Table
                currency={currency}
                rent={rent}
                color="red"
                rows={[
                  {
                    user: "USER",
                    pnl: Math.abs(profitloss) + parseFloat(rent),
                    brk: commission,
                    rent: rent,
                    total: parseFloat(profitloss) + parseFloat(commission),
                  },
                  {
                    user: "TOTAL",
                    pnl: Math.abs(profitloss) + parseFloat(rent),
                    brk: commission,
                    rent: rent,
                    total: parseFloat(profitloss) + parseFloat(commission),
                  },
                ]}
              />
            </SectionCard>
          </div>
        )}
      </div>
    </div>
  );
}

// ✅ Section Wrapper
function SectionCard({ title, color, children }) {
  const colorClass =
    color === "green"
      ? "text-[var(--buymeta)]"
      : color === "red"
      ? "text-[var(--sellmeta)]"
      : "text-[var(--primary)]";

  return (
    <div className="bg-[var(--background)] border-2 border-[var(--hover)] text-[var(--text)] rounded-xl p-4 shadow-sm">
      <div className={`font-semibold text-center mb-2 ${colorClass}`}>
        {title}
      </div>
      {children}
    </div>
  );
}

// ✅ Table Component
function Table({ rows, color, currency, rent }) {
  const pnlColor =
    color === "green"
      ? "text-[var(--buymeta)]"
      : color === "red"
      ? "text-[var(--sellmeta)]"
      : "text-[var(--text)]";


  if (!rows || rows.length === 0)
    return (
      <div className="text-center text-[12px] text-[var(--subtext)] py-2">
        No records found
      </div>
    );

  return (
    <div className="text-sm">
      {/* Header */}
      <div className={`grid ${rent > 0 ? "grid-cols-5" : "grid-cols-4"} text-[12px] text-[var(--subtext)] border-b border-[var(--hover)] pb-1 mb-2 font-medium`}>
        <span className="text-left">USER</span>
        <span className="text-right">P&L</span>
        <span className="text-right">BRK</span>
        {rent > 0 ? <span className="text-right">INT</span> : null}
        <span className="text-right">Total</span>
      </div>

      {/* Rows */}
      {rows.map((r, i) => {
        return (
          <div
            key={i}
            className={`grid ${rent > 0 ? "grid-cols-5" : "grid-cols-4"}  items-center text-[var(--text)] py-1.5 text-[13px] rounded-lg transition`}
          > 
            {/* User */}
            <span className="font-medium text-left">{r.user}</span>

            {/* P&L */}
            <span className={`${pnlColor} font-semibold text-right text-[12px]`}>
              {fmt.format(Math.abs(r.total).toFixed(2))} {currency}
            </span>

            {/* BRK always red */}
            <span className="text-[var(--sellmeta)] font-semibold text-right text-[12px]">
              {fmt.format(Math.abs(r.brk).toFixed(2))} {currency}
            </span>
            
            {rent > 0 ? (
              <span className="text-[var(--sellmeta)] font-semibold text-right text-[12px]">
                {fmt.format(Math.abs(r.rent).toFixed(2))} {currency}
              </span>
            ) : null}

            {/* TOTAL = pnl + brk */}
            <span className={`${pnlColor} font-semibold text-right text-[12px]`}>
              {fmt.format(Math.abs(r.pnl))} {currency}
            </span>
          </div>
        );
      })}
    </div>
  );
}
